import { useCallback, useEffect, useRef, useState } from "react";
import type { Page } from "@shared/contracts";
import { ApiError } from "../api/api-error";

type ListState<Item> = {
  items: Item[];
  nextCursor: string | null;
  status: "loading" | "ready" | "loading_more" | "failed" | "failed_more";
  error: ApiError | null;
};

/**
 * A list that grows a page at a time. The first page failing is a different situation
 * from a later page failing (the visitor keeps what they already have), so they are
 * separate states.
 */
export function usePagedList<Item>(
  loadPage: (cursor: string | null, signal: AbortSignal) => Promise<Page<Item>>,
) {
  const [state, setState] = useState<ListState<Item>>({
    items: [],
    nextCursor: null,
    status: "loading",
    error: null,
  });
  const inFlight = useRef<AbortController | null>(null);

  /** Starts a request. State changes only when the answer arrives. */
  const start = useCallback(
    (cursor: string | null) => {
      inFlight.current?.abort();
      const controller = new AbortController();
      inFlight.current = controller;
      loadPage(cursor, controller.signal).then(
        (page) => {
          setState((current) => ({
            items: cursor ? [...current.items, ...page.items] : page.items,
            nextCursor: page.nextCursor,
            status: "ready",
            error: null,
          }));
        },
        (error: unknown) => {
          if (controller.signal.aborted) return;
          const failure =
            error instanceof ApiError
              ? error
              : new ApiError("unexpected_response", "Something went wrong loading this.");
          setState((current) => ({
            ...current,
            status: cursor ? "failed_more" : "failed",
            error: failure,
          }));
        },
      );
    },
    [loadPage],
  );

  // The first page: the initial state already says "loading".
  useEffect(() => {
    start(null);
    return () => inFlight.current?.abort();
  }, [start]);

  /** A request the visitor asked for: say so at once, then start it. */
  const load = (cursor: string | null) => {
    setState((current) => ({
      ...current,
      status: cursor ? "loading_more" : "loading",
      error: null,
    }));
    start(cursor);
  };

  return {
    ...state,
    reload: () => load(null),
    loadMore: () => {
      if (state.nextCursor) load(state.nextCursor);
    },
  };
}
